import React, { useState } from 'react';
import { SR15_SDG_INTERACTIONS, SR15SDGInteraction } from '../../data/sr15Data';
import { SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Grid, Sparkles, Volume2, ShieldAlert, CheckCircle2 } from 'lucide-react';

interface SR15SPM4SDGsProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

export const SR15SPM4SDGs: React.FC<SR15SPM4SDGsProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['sr15_spm4'];
  const audioEngine = AudioEngine.getInstance();

  const [activeSDGId, setActiveSDGId] = useState<number | null>(null);

  const handleCellClick = (item: SR15SDGInteraction, sector: 'supply' | 'demand' | 'land') => {
    setActiveSDGId(item.sdgId);
    if (!audioEngine.getIsUnlocked()) return;

    let score = item.energySupplyScore;
    if (sector === 'demand') score = item.energyDemandScore;
    if (sector === 'land') score = item.landOceanScore;

    audioEngine.playSDGInterval(score);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>
      </div>

      {/* Main Matrix Module */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-6">
        <div className="flex items-center justify-between text-xs font-mono text-slate-400">
          <span className="flex items-center gap-2">
            <Grid className="w-4 h-4 text-cyan-400" />
            <span>Click matrix cells to audition Synergies (Major Intervals) vs Trade-offs (Minor/Tritones)</span>
          </span>
        </div>

        {/* SDG Interaction Matrix Table */}
        <div className="space-y-3">
          {SR15_SDG_INTERACTIONS.map((item) => {
            const isSelected = activeSDGId === item.sdgId;
            return (
              <div
                key={item.sdgId}
                className={`p-4 rounded-2xl border transition-all duration-200 glass-panel-hover flex flex-col md:flex-row items-start md:items-center justify-between gap-4 ${
                  isSelected
                    ? 'bg-slate-800 border-cyan-400 glow-cyan shadow-md'
                    : 'bg-slate-900/60 border-slate-800'
                }`}
              >
                <div className="w-full md:w-1/3">
                  <span className="text-xs font-bold text-white block">{item.sdgName}</span>
                </div>

                <div className="w-full md:w-2/3 grid grid-cols-3 gap-3">
                  {/* Energy Supply Cell */}
                  <button
                    onClick={() => handleCellClick(item, 'supply')}
                    className={`p-2.5 rounded-xl border text-center font-mono text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                      item.energySupplyScore > 0
                        ? 'bg-emerald-950/60 border-emerald-500/50 text-emerald-300 hover:bg-emerald-900/80'
                        : 'bg-red-950/60 border-red-500/50 text-red-300 hover:bg-red-900/80'
                    }`}
                  >
                    <span>Supply: {item.energySupplyScore > 0 ? '+' : ''}{item.energySupplyScore}</span>
                  </button>

                  {/* Energy Demand Cell */}
                  <button
                    onClick={() => handleCellClick(item, 'demand')}
                    className={`p-2.5 rounded-xl border text-center font-mono text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                      item.energyDemandScore > 0
                        ? 'bg-emerald-950/60 border-emerald-500/50 text-emerald-300 hover:bg-emerald-900/80'
                        : 'bg-red-950/60 border-red-500/50 text-red-300 hover:bg-red-900/80'
                    }`}
                  >
                    <span>Demand: {item.energyDemandScore > 0 ? '+' : ''}{item.energyDemandScore}</span>
                  </button>

                  {/* Land & Ocean Cell */}
                  <button
                    onClick={() => handleCellClick(item, 'land')}
                    className={`p-2.5 rounded-xl border text-center font-mono text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                      item.landOceanScore > 0
                        ? 'bg-emerald-950/60 border-emerald-500/50 text-emerald-300 hover:bg-emerald-900/80'
                        : 'bg-red-950/60 border-red-500/50 text-red-300 hover:bg-red-900/80'
                    }`}
                  >
                    <span>Land: {item.landOceanScore > 0 ? '+' : ''}{item.landOceanScore}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
